import { useEffect, useState, useSyncExternalStore } from 'react';
import type { Attachment, DocNode } from '@/shared/api/schemas';

/** Сколько можно отменить отправку. Сервер ждёт чуть дольше — с запасом на дорогу. */
export const UNSEND_MS = 15_000;

interface Sent {
  readonly id: string;
  readonly cid: string;
  readonly threadRootId: string | null;
  readonly deadline: number;
}

// Время считаем по часам этого устройства с момента, когда сервер принял сообщение, — не по часам сервера.
let sent: readonly Sent[] = [];
const watchers = new Set<() => void>();

function changed(): void {
  watchers.forEach((w) => {
    w();
  });
}

function subscribe(watcher: () => void): () => void {
  watchers.add(watcher);
  return () => {
    watchers.delete(watcher);
  };
}

/** Сообщение ушло: следующие 15 секунд его можно вернуть в поле ввода. */
export function rememberSent(id: string, cid: string, threadRootId: string | null, now = Date.now()): void {
  sent = [...sent.filter((x) => x.deadline > now && x.id !== id), { id, cid, threadRootId, deadline: now + UNSEND_MS }];
  changed();
}

export function forgetSent(id: string): void {
  if (!sent.some((x) => x.id === id)) return;
  sent = sent.filter((x) => x.id !== id);
  changed();
}

export function canUnsend(id: string, now = Date.now()): boolean {
  return sent.some((x) => x.id === id && x.deadline > now);
}

/** Последнее своё сообщение в этой беседе (или треде), которое ещё можно отменить. */
export function lastUnsendable(cid: string, threadRootId: string | null, now = Date.now()): string | null {
  const mine = sent.filter((x) => x.cid === cid && x.threadRootId === threadRootId && x.deadline > now);
  return mine.at(-1)?.id ?? null;
}

/** Пункт «Отменить отправку» в меню: виден 15 секунд и сам исчезает. */
export function useCanUnsend(id: string): boolean {
  const deadline = useSyncExternalStore(subscribe, () => sent.find((x) => x.id === id)?.deadline ?? null);
  const [passed, setPassed] = useState<number | null>(null);
  useEffect(() => {
    if (deadline === null) return;
    const timer = setTimeout(() => {
      setPassed(deadline);
    }, Math.max(0, deadline - Date.now()));
    return () => {
      clearTimeout(timer);
    };
  }, [deadline]);
  return deadline !== null && passed !== deadline;
}

/** Что вернулось после отмены: текст как был и файлы — их можно отправить заново. */
export interface Restored {
  readonly content: DocNode | null;
  readonly text: string;
  readonly attachments: readonly Attachment[];
}

type Listener = (restored: Restored) => void;
const listeners = new Map<string, Listener>();
const waiting = new Map<string, Restored>();
const slot = (cid: string, threadRootId: string | null) => `${cid}|${threadRootId ?? ''}`;

/** Вернуть текст и файлы в поле ввода беседы. Если поле сейчас не на экране — дождутся его. */
export function restoreToComposer(cid: string, threadRootId: string | null, restored: Restored): void {
  const listener = listeners.get(slot(cid, threadRootId));
  if (listener === undefined) waiting.set(slot(cid, threadRootId), restored);
  else listener(restored);
}

export function onRestore(cid: string, threadRootId: string | null, listener: Listener): () => void {
  const key = slot(cid, threadRootId);
  listeners.set(key, listener);
  const left = waiting.get(key);
  if (left !== undefined) {
    waiting.delete(key);
    queueMicrotask(() => {
      listener(left);
    });
  }
  return () => {
    if (listeners.get(key) === listener) listeners.delete(key);
  };
}

/** Для тестов: забыть всё. */
export function resetUnsend(): void {
  sent = [];
  waiting.clear();
  changed();
}
